import type { FC } from 'react'
import type { GrandNextLinkProps } from 'ui'
import { GrandNextLink, GrandTooltip, ImageBox } from 'ui'
import { Box, Flex, Icon, IconButton, VStack } from '@chakra-ui/react'
import { BiPhotoAlbum } from 'react-icons/bi'
import { httpImageFactory } from 'api'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import type { UIVariant } from 'ecosystem'
import { useProductContext } from '../../hooks/useProductContext'

interface ProductVariantItemProps extends Omit<GrandNextLinkProps, 'href'> {
  item: UIVariant
  columns: number
  isSelected: boolean
  isAvailable: boolean
}

export interface VariantItemOverrides {
  noMedias?: string
  withMedias?: string
}

const VariantItem: FC<ProductVariantItemProps> = ({
  item,
  columns,
  isSelected,
  isAvailable,
  ...props
}) => {
  const { onOpenMedias } = useProductContext()
  const overrides = useOverridesContext<keyof VariantItemOverrides>()

  const handleViewImages = () => {
    item.medias?.length && onOpenMedias(item.medias, 0)
  }

  const mediasLabel = !item.medias?.length
    ? overrideText('inga bilder', overrides?.noMedias)
    : overrideText('se bilder', overrides?.withMedias)

  return (
    <VStack
      _hover={{
        '& .variant-item__gallery-button': {
          visibility: 'visible',
          opacity: 1
        }
      }}
      align="flex-start"
      className="variant-item"
      position="relative"
      spacing={0}
      w={`${100 / columns}%`}>
      {isAvailable ? (
        <GrandTooltip label={mediasLabel}>
          <IconButton
            aria-label={mediasLabel}
            className="variant-item__gallery-button"
            icon={<Icon as={BiPhotoAlbum} />}
            isDisabled={!item.medias?.length}
            onClick={handleViewImages}
            opacity="0"
            position="absolute"
            right="0"
            size="xs"
            top="0"
            transform="translate(25%, -25%)"
            transition="all 0.05s cubic-bezier(.17,.67,.83,.67)"
            visibility="hidden"
            zIndex="1"
          />
        </GrandTooltip>
      ) : null}

      <Box
        bg="background.default"
        borderColor={isSelected ? 'primary' : 'transparent'}
        borderRadius="sm"
        borderWidth="2px"
        cursor={isAvailable ? 'pointer' : 'not-allowed'}
        h={{ base: '66px', sm: '90px', md: '66px', lg: '74px' }}
        p={2}
        position="relative"
        w="full">
        {!isAvailable && (
          <Flex
            align="center"
            bg="whiteAlpha.800"
            height="full"
            justify="center"
            position="absolute"
            w="full"
            zIndex="overlay"
          />
        )}

        <GrandNextLink
          _focus={{ outline: 'none' }}
          aria-label={`Image with link to product: ${item.name}`}
          href={item.path}
          key={`variant-${item.id}`}
          transition="all 0.05s cubic-bezier(.17,.67,.83,.67)"
          {...props}>
          <GrandTooltip label={item.name || ''} offset={[0, 8]}>
            <ImageBox
              _hover={{
                opacity: '.75'
              }}
              h="100%"
              imageProps={{
                alt: item.name,
                title: '',
                sizes: '5vw',
                src: httpImageFactory(item.imageUrl || ''),
                style: {
                  objectFit: 'contain'
                }
              }}
            />
          </GrandTooltip>
        </GrandNextLink>
      </Box>
    </VStack>
  )
}

export default VariantItem
